import { ColumnType } from "@/queries/smart-tables/types";

interface CompositionColumnLike {
  id: string;
  title?: string;
  type?: ColumnType | string;
  config?: Record<string, unknown> | null;
}

export interface CompositionSourceDefinition {
  parentColumnId: string;
  source: string;
  childField: string;
  title: string;
  type?: ColumnType;
  index: number;
}

interface CompositionColumnTarget<TColumn extends CompositionColumnLike = CompositionColumnLike> {
  storageColumnId: string;
  displayColumnId: string;
  column?: TColumn;
  source?: CompositionSourceDefinition;
}

interface CompositionColumnLookup<TColumn extends CompositionColumnLike = CompositionColumnLike> {
  get(columnId: string): TColumn | undefined;
  values(): Iterable<TColumn>;
}

interface CompositionSourceValueOptions {
  errorMessage?: string | null;
  failed?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isColumnType = (value: unknown): value is ColumnType =>
  typeof value === "string" && Object.values(ColumnType).includes(value as ColumnType);

export const resolveColumnType = (
  value: unknown,
  fallback?: ColumnType,
): ColumnType | undefined => {
  if (isColumnType(value)) {
    return value;
  }

  return fallback;
};

const isCompositionColumn = (
  column: CompositionColumnLike | null | undefined,
): column is CompositionColumnLike => {
  if (!column) {
    return false;
  }

  return resolveColumnType(column.type) === ColumnType.COMPOSITION;
};

const getCompositionChildField = (parentColumnId: string, source: string): string =>
  `${parentColumnId}_${source}`;

const getConfigArray = (
  config: Record<string, unknown> | null | undefined,
  key: string,
): readonly unknown[] => {
  const value = config?.[key];
  return Array.isArray(value) ? value : [];
};

export const getCompositionSourceDefinitions = (
  column: CompositionColumnLike | null | undefined,
): CompositionSourceDefinition[] => {
  if (!isCompositionColumn(column)) {
    return [];
  }

  const sources = getConfigArray(column.config, "sources");
  if (sources.length === 0) {
    return [];
  }

  const sourceTitles = getConfigArray(column.config, "source_titles");
  const sourceTypes = getConfigArray(column.config, "source_types");

  return sources.reduce<CompositionSourceDefinition[]>((definitions, source, index) => {
    if (typeof source !== "string" || source.length === 0) {
      return definitions;
    }

    const rawTitle = sourceTitles[index];
    const rawType = sourceTypes[index];

    definitions.push({
      parentColumnId: column.id,
      source,
      childField: getCompositionChildField(column.id, source),
      title: typeof rawTitle === "string" && rawTitle.length > 0 ? rawTitle : `Source ${index + 1}`,
      type: resolveColumnType(rawType),
      index,
    });

    return definitions;
  }, []);
};

const getCompositionSourceDefinition = (
  column: CompositionColumnLike | null | undefined,
  source: string,
): CompositionSourceDefinition | undefined =>
  getCompositionSourceDefinitions(column).find(definition => definition.source === source);

export const getCompositionSourceTitle = (
  column: CompositionColumnLike | null | undefined,
  source: string,
): string | undefined => getCompositionSourceDefinition(column, source)?.title;

export const getCompositionSourceType = (
  column: CompositionColumnLike | null | undefined,
  source: string,
): ColumnType | undefined => getCompositionSourceDefinition(column, source)?.type;

export const resolveCompositionColumnDisplayTitle = (
  column: CompositionColumnLike | null | undefined,
  fallbackTitle?: string,
): string | undefined => {
  if (!column) {
    return fallbackTitle;
  }

  const sourceDefinitions = getCompositionSourceDefinitions(column);
  if (sourceDefinitions.length === 1) {
    return sourceDefinitions[0]?.title ?? column.title ?? fallbackTitle;
  }

  return column.title ?? fallbackTitle;
};

const COMPOSITION_UNWRAP_KEYS = ["value", "text", "output", "content"] as const;
const MAX_COMPOSITION_UNWRAP_DEPTH = 6;

const maybeParseJsonLikeString = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (trimmed.length < 2) {
    return value;
  }

  const startsLikeJson = trimmed.startsWith("{") || trimmed.startsWith("[");
  const endsLikeJson = trimmed.endsWith("}") || trimmed.endsWith("]");

  if (!startsLikeJson || !endsLikeJson) {
    return value;
  }

  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return value;
  }
};

const unwrapCompositionPayload = (value: unknown): unknown => {
  let current = value;

  for (let index = 0; index < MAX_COMPOSITION_UNWRAP_DEPTH; index += 1) {
    const parsed = maybeParseJsonLikeString(current);
    if (!Object.is(parsed, current)) {
      current = parsed;
      continue;
    }

    if (!isRecord(current)) {
      return current;
    }

    const matchingKeys = COMPOSITION_UNWRAP_KEYS.filter(key =>
      Object.prototype.hasOwnProperty.call(current, key),
    );

    if (matchingKeys.length !== 1) {
      return current;
    }

    const inner = current[matchingKeys[0]];
    if (Object.is(inner, current)) {
      return current;
    }

    current = inner;
  }

  return current;
};

export const normalizeCompositionDisplayValue = (value: unknown): unknown =>
  maybeParseJsonLikeString(value);

const getCompositionSourceValue = (
  normalizedParentValue: unknown,
  source: string,
  options: CompositionSourceValueOptions = {},
): unknown => {
  if (options.errorMessage || options.failed) {
    return normalizedParentValue ?? null;
  }

  if (!isRecord(normalizedParentValue)) {
    return null;
  }

  return Object.prototype.hasOwnProperty.call(normalizedParentValue, source)
    ? unwrapCompositionPayload(normalizedParentValue[source])
    : null;
};

export const resolveCompositionDisplayValue = (
  column: CompositionColumnLike | null | undefined,
  parentValue: unknown,
  fallbackValue: unknown = parentValue,
  options: CompositionSourceValueOptions = {},
): unknown => {
  const normalizedParentValue = isCompositionColumn(column)
    ? normalizeCompositionDisplayValue(parentValue)
    : parentValue;
  const sourceDefinitions = getCompositionSourceDefinitions(column);

  if (options.errorMessage || options.failed) {
    return fallbackValue ?? normalizedParentValue;
  }

  if (sourceDefinitions.length !== 1) {
    return normalizedParentValue;
  }

  const [definition] = sourceDefinitions;
  if (!isRecord(normalizedParentValue)) {
    return fallbackValue ?? normalizedParentValue;
  }

  return Object.prototype.hasOwnProperty.call(normalizedParentValue, definition.source)
    ? unwrapCompositionPayload(normalizedParentValue[definition.source])
    : unwrapCompositionPayload(fallbackValue ?? normalizedParentValue);
};

export const assignCompositionSourceFields = (
  row: Record<string, unknown>,
  column: CompositionColumnLike | null | undefined,
  parentValue: unknown,
  options: CompositionSourceValueOptions = {},
): unknown => {
  const normalizedParentValue = isCompositionColumn(column)
    ? normalizeCompositionDisplayValue(parentValue)
    : parentValue;

  const sourceDefinitions = getCompositionSourceDefinitions(column);

  for (const definition of sourceDefinitions) {
    row[definition.childField] = getCompositionSourceValue(
      normalizedParentValue,
      definition.source,
      options,
    );
  }

  return normalizedParentValue;
};

const getLookupValue = <TColumn extends CompositionColumnLike>(
  columns: CompositionColumnLookup<TColumn> | Iterable<TColumn> | null | undefined,
  columnId: string,
): TColumn | undefined => {
  if (!columns) {
    return undefined;
  }

  if (
    "get" in Object(columns) &&
    typeof (columns as CompositionColumnLookup<TColumn>).get === "function"
  ) {
    return (columns as CompositionColumnLookup<TColumn>).get(columnId);
  }

  for (const column of columns as Iterable<TColumn>) {
    if (column.id === columnId) {
      return column;
    }
  }

  return undefined;
};

const getLookupValues = <TColumn extends CompositionColumnLike>(
  columns: CompositionColumnLookup<TColumn> | Iterable<TColumn> | null | undefined,
): Iterable<TColumn> => {
  if (!columns) {
    return [];
  }

  if (
    "values" in Object(columns) &&
    typeof (columns as CompositionColumnLookup<TColumn>).values === "function"
  ) {
    return (columns as CompositionColumnLookup<TColumn>).values();
  }

  return columns as Iterable<TColumn>;
};

export const resolveCompositionColumnTarget = <TColumn extends CompositionColumnLike>(
  displayColumnId: string,
  columns: CompositionColumnLookup<TColumn> | Iterable<TColumn> | null | undefined,
): CompositionColumnTarget<TColumn> => {
  const directColumn = getLookupValue(columns, displayColumnId);

  if (directColumn) {
    return {
      storageColumnId: displayColumnId,
      displayColumnId,
      column: directColumn,
    };
  }

  for (const column of getLookupValues(columns)) {
    const source = getCompositionSourceDefinitions(column).find(
      definition => definition.childField === displayColumnId,
    );

    if (source) {
      return {
        storageColumnId: column.id,
        displayColumnId,
        column,
        source,
      };
    }
  }

  return {
    storageColumnId: displayColumnId,
    displayColumnId,
  };
};
